import { defineStore } from 'pinia'
import { computed } from 'vue'
import { useWhatsAppService } from '../services/useWhatsAppService'
import type {
  MessageEventData,
  MessageStatusEventData,
  UserSendTemplateMessageEvent,
  NewChatEvent,
  NewChatTemplateEvent,
  UserEntryChatEvent,
  UserTookChatEvent,
  UserExitChatEvent,
  InteractionEvent,
  TransferRequestEvent,
  TransferAcceptedEvent,
  ChatTransferredToCampaignEvent,
  ChatTransferredToBotEvent,
  ChatFinishedEvent,
  ChatUpdatedEvent,
  ChatClosedByTimeoutClientEvent,
  ChatReleasedByUserTimeoutEvent,
  ChatReleasedByAgentLogoutEvent,
  ChatReleasedByBulkTemplateEvent,
  ChatExpiredByMetaWindowEvent,
  TemplateSendBlockedEvent,
  TemplateSendFailedEvent,
  AgentEjectedFromCampaignsEvent,
  AgentPauseChangedBySupervisorEvent,
} from '../types/interfaces'
import { UserRole } from '../types/enums'

// Tipos de eventos del WebSocket
type WebSocketEventData =
  | MessageEventData
  | MessageStatusEventData
  | UserSendTemplateMessageEvent
  | NewChatEvent
  | NewChatTemplateEvent
  | UserEntryChatEvent
  | UserTookChatEvent
  | UserExitChatEvent
  | InteractionEvent
  | TransferRequestEvent
  | TransferAcceptedEvent
  | ChatTransferredToCampaignEvent
  | ChatTransferredToBotEvent
  | ChatFinishedEvent
  | ChatUpdatedEvent
  | ChatClosedByTimeoutClientEvent
  | ChatReleasedByUserTimeoutEvent
  | ChatReleasedByAgentLogoutEvent
  | ChatReleasedByBulkTemplateEvent
  | ChatExpiredByMetaWindowEvent
  | TemplateSendBlockedEvent
  | TemplateSendFailedEvent
  | AgentEjectedFromCampaignsEvent
  | AgentPauseChangedBySupervisorEvent
  | ChatFinishedEvent

export const useWebSocketStore = defineStore('websocket', () => {
  const whatsAppService = useWhatsAppService()
  let initializationPromise: Promise<void> | null = null
  let recoveryPromise: Promise<void> | null = null

  // Estado de conexión
  const isConnected = computed(() => whatsAppService.isConnected.value)
  const isReconnecting = computed(() => whatsAppService.isReconnecting.value)
  const reconnectAttempts = computed(() => whatsAppService.reconnectAttempts.value)
  const currentReconnectDelay = computed(() => whatsAppService.currentReconnectDelay.value)

  const initializeWebSocket = async (userId: string, role: UserRole = UserRole.OPERATOR) => {
    if (whatsAppService.isConnected.value) {
      return
    }

    if (initializationPromise) {
      return initializationPromise
    }

    initializationPromise = (async () => {
      await whatsAppService.connect(userId, role)
    })()

    try {
      await initializationPromise
    } finally {
      initializationPromise = null
    }
  }

  const disconnect = () => {
    whatsAppService.disconnect()
  }

  const sendAgentLogout = (userId: string) => {
    whatsAppService.sendAgentLogout(userId)
  }

  const manualReconnect = async () => {
    if (recoveryPromise) {
      return recoveryPromise
    }

    recoveryPromise = whatsAppService.manualReconnect()
    try {
      await recoveryPromise
    } finally {
      recoveryPromise = null
    }
  }

  const probeConnection = (timeoutMs?: number) => whatsAppService.probeConnection(timeoutMs)

  const onConnectionStateChange = (handler: (data: { state: string; data?: unknown }) => void) => {
    whatsAppService.onConnectionStateChange(handler)
  }

  // Passthrough directo al Map de handlers de whatsAppService: sin Map ni
  // relay propios. Antes había dos capas de suscripción (servicio y store)
  // con limpieza independiente en disconnect(); un desajuste entre ambas
  // causó un bug de duplicación de eventos tras un ciclo logout/login.
  // Con una sola capa, solo hay un lugar que limpiar y un solo registro por
  // handler.
  const on = (event: string, handler: (data: WebSocketEventData) => void) => {
    whatsAppService.on(event, handler as (data: unknown) => void)
  }

  const off = (event: string, handler: (data: WebSocketEventData) => void) => {
    whatsAppService.off(event, handler as (data: unknown) => void)
  }

  // Acceso directo al servicio para comandos complejos
  const getService = () => whatsAppService

  return {
    // Estado
    isConnected,
    isReconnecting,
    reconnectAttempts,
    currentReconnectDelay,

    // Métodos
    initializeWebSocket,
    disconnect,
    sendAgentLogout,
    manualReconnect,
    probeConnection,
    on,
    off,
    onConnectionStateChange,

    // Acceso al servicio
    getService,
  }
})
